/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import { LineBarPlotTypeSelector } from "components/charts/modern/inputs/PlotTypeSelector";
import TickerSymbolSelector from "components/charts/modern/inputs/TickerSymbolSelector";
import KeyValueTooltip from "components/charts/modern/tooltip/KeyValueTooltip";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { useMinWidthTailwindBreakpoints } from "components/ui/hooks/useTailwindBreakpoints";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import getYear from "date-fns/getYear";
import parse from "date-fns/parse";
import parseISO from "date-fns/parseISO";
import type Highcharts from "highcharts/highstock";
import type { SeriesBarOptions, SeriesLineOptions } from "highcharts/highstock";
import FreeFloatHistoryQuery from "lib/graphql/queries/FreeFloatHistoryQuery";
import type {
  IFreeFloatHistoryQuery,
  IFreeFloatHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/FreeFloatHistoryQuery.generated";
import { useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import useTheme from "components/ui/useTheme";
import classNames from "components/ui/classNames";

interface FreeFloatChartProps {
  tickerSymbols: string[];
  availableTickerSymbols?: string[];
  onToggleTickerSymbols?: (tickerSymbol: string[]) => void;
}

export default function FreeFloatChart({
  tickerSymbols,
  availableTickerSymbols,
  onToggleTickerSymbols,
}: FreeFloatChartProps) {
  const intl = useIntl();
  const theme = useTheme();
  const breakpoints = useMinWidthTailwindBreakpoints();
  const [plotType, setPlotType] = useState<"line" | "column">("line");

  // Theme-aware colors
  const slateOrDarkSlate = theme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = theme === "dark" ? colors.slate[300] : colors.slate[500];
  const tooltipBgColor = theme === "dark" ? colors.slate[800] : colors.white;
  const tooltipBorderColor = theme === "dark" ? colors.slate[950] : colors.slate[200];
  const hoverBorderColor = theme === "dark" ? colors.slate[400] : colors.slate[500];
  const hoverFillColor = theme === "dark" ? colors.slate[700] : colors.slate[50];
  const lineColor = theme === "dark" ? colors.slate[400] : colors.slate[600];
  const markerFillColor = theme === "dark" ? colors.slate[900] : colors.slate[50];
  const barFillColor = theme === "dark" ? colors.slate[700] : colors.slate[100];
  const barBorderColor = theme === "dark" ? colors.slate[400] : colors.slate[600];
  const barHoverColor = theme === "dark" ? colors.slate[500] : colors.slate[700];
  const tickerSymbolsForSeries = [...tickerSymbols];
  const selectedTickerSymbol = tickerSymbolsForSeries[0];
  if (!selectedTickerSymbol) {
    throw new Error("Couldn't locate the selected ticker symbol");
  }
  const { data, previousData, loading } = useQuery<IFreeFloatHistoryQuery, IFreeFloatHistoryQueryVariables>(
    FreeFloatHistoryQuery,
    {
      variables: {
        tickerSymbol: tickerSymbols[0],
      },
    },
  );
  const freeFloatHistory = (loading && !data ? previousData?.freeFloatHistory : data?.freeFloatHistory) || [];

  const hasFreeFloatHistory = freeFloatHistory.length > 0;

  // If the size is equal to 1, plot it as a single bar.
  const onlyPlotTypeBar = freeFloatHistory.length === 1;
  let plotTypeOption: "bar" | "line" = plotType === "line" ? "line" : "bar";

  if (onlyPlotTypeBar) {
    plotTypeOption = "bar";
  }

  const series: (SeriesLineOptions | SeriesBarOptions)[] = [
    {
      showInNavigator: true,
      type: plotTypeOption,
      animation: false,
      name: selectedTickerSymbol,
      data: freeFloatHistory
        .filter(
          (node) => node.floatShareToTotalSharesOutstanding !== null && node.floatShareToTotalSharesOutstanding !== 0,
        )
        .map((node) => ({
          x: getYear(parseISO(node.asOfDate)),
          y: node.floatShareToTotalSharesOutstanding,
          custom: node,
        }))
        .sort((a, b) => compareAsc(a.x, b.x)),
      custom: {
        tickerSymbol: selectedTickerSymbol,
        primaryColor: colors.sky[50],
        secondaryColor: colors.sky[600],
      },
    },
  ];

  const labels = {
    sharesOutstanding: intl.formatMessage({
      defaultMessage: "Ações em Circulação",
      id: "psT3CN",
    }),
    freeFloat: intl.formatMessage({
      defaultMessage: "Free Float",
      id: "bQN4OR",
    }),
  };
  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        format: "{value}",
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
    },
    yAxis: {
      type: "linear",
      // min: minValue,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      labels: {
        enabled: true,
        align: "center",
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
        formatter: function () {
          const percentageFormatter = new Intl.NumberFormat("en-US", {
            style: "percent",
            maximumFractionDigits: 2,
          });

          return percentageFormatter.format(Number(this.value));
        },
      },
      tickWidth: 0,
      showFirstLabel: true,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series: [...series],
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: tooltipBgColor,
      borderColor: tooltipBorderColor,
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const node = (this.point as any).custom as (typeof freeFloatHistory)[number] | null | undefined;
        if (!this.key || !node || node.__typename !== "FreeFloatHistoryItem") {
          return "";
        }

        const percentageFormatter = new Intl.NumberFormat("en-US", {
          style: "percent",
          maximumFractionDigits: 2,
        });

        return renderToStaticMarkup(
          <KeyValueTooltip
            x={intl.formatDate(parse(node.asOfDate, "yyyy-MM-dd", new Date()), {
              year: "numeric",
              month: "numeric",
              day: "numeric",
            })}
            keyValues={[
              {
                key: labels.freeFloat,
                value: percentageFormatter.format(node.floatShareToTotalSharesOutstanding),
              },
            ]}
          />,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: hoverBorderColor,
            color: hoverFillColor,
            lineWidth: 2,
          },
        },
        dataLabels: {
          enabled: false,
          crop: false,
          style: {
            color: labelsColor,
            fontFamily: "novel-mono-pro-condensed",
            fontWeight: "600",
            fontSize: "10px",
            padding: "8px",
          },
          inside: false,
          formatter: function () {
            const node = (this.point as any).custom as (typeof freeFloatHistory)[number] | null | undefined;
            if (!this.key || !node || node.__typename !== "FreeFloatHistoryItem") {
              return "";
            }
            const percentageFormatter = new Intl.NumberFormat("en-US", {
              style: "percent",
              maximumFractionDigits: 2,
            });
            return percentageFormatter.format(node.floatShareToTotalSharesOutstanding);
          },
        },
      },
      line: {
        color: lineColor,
        lineWidth: 2,
        dataLabels: {
          enabled: false,
        },
        marker: {
          enabled: false,
          fillColor: markerFillColor,
          lineColor: lineColor,
          lineWidth: 4,
          width: 8,
          height: 8,
        },
      },
      bar: {
        color: barFillColor,
        borderWidth: 1.5,
        borderColor: barBorderColor,
        states: {
          hover: {
            color: barHoverColor,
            borderColor: barHoverColor,
          },
        },
        borderRadius: {
          where: "end",
          radius: 12,
          scope: "stack",
        },
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            callback: () => true,
          },
          chartOptions: {
            plotOptions: {
              column: {
                dataLabels: {
                  enabled: false,
                },
              },
            },
          },
        },
        {
          condition: {
            callback: () => {
              return breakpoints.lg || breakpoints.xl;
            },
          },
          chartOptions: {
            plotOptions: {
              column: {
                dataLabels: {
                  enabled: true,
                },
              },
            },
          },
        },
      ],
    },
  };

  if (loading && !hasFreeFloatHistory) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  return (
    <OnlyPremiumRender>
      <div className="flex h-full w-full flex-col space-y-4">
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex w-full flex-col items-center space-y-4 md:flex-row md:space-x-4 md:space-y-0">
            <div className="contents lg:hidden">
              <BrazilianTagAlong />
            </div>

            {onToggleTickerSymbols && availableTickerSymbols && availableTickerSymbols.length >= 2 && (
              <TickerSymbolSelector
                tickerSymbols={availableTickerSymbols}
                value={tickerSymbols}
                onChange={(tickerSymbols) => onToggleTickerSymbols(tickerSymbols)}
              />
            )}

            {!onlyPlotTypeBar && <LineBarPlotTypeSelector value={plotType} onChange={setPlotType} />}
          </div>

          <div className="hidden lg:inline-flex">
            <BrazilianTagAlong />
          </div>
        </div>

        {hasFreeFloatHistory ? (
          <HighchartsChart options={options} className="h-96 xl:h-full" />
        ) : (
          <div className="flex h-96 w-80 items-center justify-center sm:w-full">
            <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-gray-400">
              <div>
                <SadFaceIcon />
              </div>

              <p>
                <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
              </p>
            </div>
          </div>
        )}
      </div>
    </OnlyPremiumRender>
  );
}

function BrazilianTagAlong() {
  const issuer = useRootIssuer();

  if (issuer.__typename !== "BrazilianCompanyIssuer") {
    return null;
  }

  const { tagAlong } = issuer;

  if (!tagAlong) {
    return null;
  }

  return (
    <div
      className={classNames(
        "flex w-full justify-center space-x-1 whitespace-nowrap rounded-lg bg-slate-100 px-2 py-1.5",
        "md:w-fit",
        "dark:bg-slate-950",
      )}
    >
      <span className="text-xs font-medium md:text-sm">Tag Along:</span>

      <p className={classNames("flex gap-2")}>
        {tagAlong.commonStock !== null && (
          <span
            aria-label={`${tagAlong.commonStock}% de Tag Along nas Ações Ordinárias`}
            className="px-1 text-xs font-medium md:text-sm"
            style={{
              color: getColorByPercentage(tagAlong.commonStock),
            }}
          >
            {tagAlong.commonStock}% ON
          </span>
        )}

        {tagAlong.preferredStock !== null && (
          <span
            aria-label={`${tagAlong.preferredStock}% de Tag Along nas Ações Preferenciais`}
            className="px-1 text-xs font-medium md:text-sm"
            style={{
              color: getColorByPercentage(tagAlong.preferredStock),
            }}
          >
            {tagAlong.preferredStock}% PN
          </span>
        )}

        {tagAlong.unitStock !== null && (
          <span
            aria-label={`${tagAlong.preferredStock}% de Tag Along nas UNITs`}
            className="px-1 text-xs font-medium md:text-sm"
            style={{
              color: getColorByPercentage(tagAlong.unitStock),
            }}
          >
            {tagAlong.unitStock}% UNIT
          </span>
        )}
      </p>
    </div>
  );
}

function getColorByPercentage(tagAlongPercentage: number): string {
  if (tagAlongPercentage === 100) {
    return colors.emerald[500];
  }

  if (tagAlongPercentage >= 80 && tagAlongPercentage < 100) {
    return colors.amber[600];
  }

  return colors.red[500];
}
